// src/hooks/useGeneratedPrayerAudio.ts
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import useTextToSpeech, { SpeakOptions } from '@/hooks/useTextToSpeech';
import { TtsFormat } from '@/components/audio/services/AbielAudioService';
import { DEFAULT_VOICE } from '@/constants/voiceConstants';

/** Minimal shape we accept from various prayer creators/responses */
export type PrayerLike = {
  id?: string | number | null;
  /** Common fields observed across the app; we pick the first non-empty */
  content?: string | null;
  prayer?: string | null;
  message?: string | null;
  text?: string | null;
};

export type UseGeneratedPrayerAudioOptions = {
  /** Auto-play when a new prayer arrives. Default: true */
  auto?: boolean;
  /** Optional TTS defaults (voice/model/format) */
  speak?: SpeakOptions & { format?: TtsFormat };
  /** Prefix used for the audio session id. Default: 'prayer' */
  sessionPrefix?: string;
  /**
   * Optional guard to skip very short/empty content (after trimming).
   * Default: 12 characters (to avoid accidental noise).
   */
  minLength?: number;
  /**
   * If true, auto-play only when the user explicitly asked to include audio.
   * You can wire this to a UI toggle if desired. Default: false (not enforced).
   */
  requireUserAudioIntent?: boolean;
  /** If provided, must be true to permit auto-play (e.g., a user toggle). */
  userAudioIntent?: boolean;
};

export type UseGeneratedPrayerAudioReturn = {
  /** Triggers TTS for the current prayer content and plays via audio context. */
  playNow: () => Promise<string | null>;
  /** Stops current audio session (if supported by context). */
  stop: () => Promise<void> | void;
  /** True while fetching TTS or handing off to audio context. */
  isLoading: boolean;
  /** Last produced Blob URL (if any). */
  url: string | null;
  /** Latest error (stringified) */
  error: string | null;
  /** Whether environment can autoplay (from audio context). */
  canAutoplay: boolean;
  /** Whether an auto-play was attempted for the current prayer. */
  didAutoplay: boolean;
  /** The computed session id (stable for this prayer). */
  sessionId: string | null;
};

/** Internal: stable hash for change detection (not cryptographic). */
function simpleHash(s: string): string {
  let h = 0, i = 0, len = s.length;
  while (i < len) h = ((h << 5) - h + s.charCodeAt(i++)) | 0;
  return String(h >>> 0);
}

/** Internal: get the first non-empty content field. */
function pickPrayerText(p?: PrayerLike | null | undefined): string {
  const candidate =
    (p?.content ?? '') ||
    (p?.prayer ?? '') ||
    (p?.message ?? '') ||
    (p?.text ?? '');
  return (candidate || '').toString().trim();
}

/**
 * Auto-plays generated prayer audio (optionally) and exposes manual controls.
 * Uses the binary-safe TTS pipeline under the hood.
 */
export function useGeneratedPrayerAudio(
  prayer: PrayerLike | null | undefined,
  opts: UseGeneratedPrayerAudioOptions = {}
): UseGeneratedPrayerAudioReturn {
  const {
    auto = true,
    speak: speakDefaults,
    sessionPrefix = 'prayer',
    minLength = 12,
    requireUserAudioIntent = false,
    userAudioIntent,
  } = opts;

  const { speak, stop, url, isLoading, error, canAutoplay } = useTextToSpeech({
    defaultVoice: speakDefaults?.voice ?? DEFAULT_VOICE,
    defaultModel: speakDefaults?.model ?? 'gpt-4o-mini-tts',
    defaultFormat: speakDefaults?.format ?? 'mp3',
    keepLastUrl: true,
  });

  const text = useMemo(() => pickPrayerText(prayer), [prayer]);
  const isPlayableText = text.length >= minLength;

  // Session id: keep stable for the same prayer id+text content
  const sessionId = useMemo(() => {
    if (!isPlayableText) return null;
    const base = `${sessionPrefix}:${prayer?.id ?? 'no-id'}`;
    // If there's no id or id is reused, include a content hash to disambiguate
    const tag = simpleHash(`${prayer?.id ?? ''}::${text}`);
    return `${base}:${tag}`;
  }, [isPlayableText, sessionPrefix, prayer?.id, text]);

  // Avoid double auto-plays for the same prayer (id+text combo)
  const lastSignatureRef = useRef<string>('');
  const [didAutoplay, setDidAutoplay] = useState(false);

  const signature = useMemo(() => {
    return sessionId ? `${sessionId}` : '';
  }, [sessionId]);

  const shouldAutoPlay = useMemo(() => {
    if (!auto) return false;
    if (!isPlayableText || !sessionId) return false;
    if (requireUserAudioIntent && !userAudioIntent) return false;
    return true;
  }, [auto, isPlayableText, sessionId, requireUserAudioIntent, userAudioIntent]);

  const playNow = useCallback(async (): Promise<string | null> => {
    if (!isPlayableText || !sessionId) return null;
    const url = await speak(sessionId, text, speakDefaults);
    return url;
  }, [isPlayableText, sessionId, text, speak, speakDefaults]);

  // Auto-play when a new prayer arrives
  useEffect(() => {
    if (!shouldAutoPlay) {
      setDidAutoplay(false);
      return;
    }

    if (signature && signature !== lastSignatureRef.current) {
      lastSignatureRef.current = signature;

      // Fire and forget; errors are surfaced via `error` state
      playNow()
        .then(() => setDidAutoplay(true))
        .catch(() => setDidAutoplay(false));
    }
  }, [shouldAutoPlay, signature, playNow]);

  return {
    playNow,
    stop,
    isLoading,
    url,
    error,
    canAutoplay,
    didAutoplay,
    sessionId,
  };
}

export default useGeneratedPrayerAudio;
